import { Account } from "@shared/models";
import { useDispatch, useSelector } from "react-redux";
import * as contactStore from "@containers/Contact/store";
import * as accountStore from "@containers/Account/store";
import { actions as sharedActions } from "@shared/store";
import React, { useCallback, useEffect } from "react";
import { MODAL_TYPES } from "@shared/constants";
import { CompanyForm } from "@containers/Account/components/CompanyCreateEditContainer/CompanyForm";
import { CreateAccountInterface } from "@containers/Account/interface/CreateAccount.interface";
import { actions as AccountActions } from "@containers/Account/store";

interface CompanyCreateEditContainerProps {
  onClose: () => void;
  account?: Account;
  data?: {
    preserveOnClose?: boolean;
  };
  callback?: () => void;
}

const CompanyCreateEditContainer = (props: CompanyCreateEditContainerProps) => {
  const { onClose, account, data, callback } = props;
  const dispatch = useDispatch();

  const selectedAccount = useSelector(accountStore.selectors.getSelectedAccount());

  useEffect(() => {
    if (account && !selectedAccount) {
      dispatch(accountStore.actions.getAccount.request({ id: account.id }));
    }
  }, [dispatch, account, selectedAccount]);

  useEffect(() => {
    return () => {
      if (!data?.preserveOnClose) {
        dispatch(accountStore.actions.clearAccount());
        dispatch(contactStore.actions.clearStates());
        dispatch(contactStore.actions.clearCounties());
      }
    };
  }, [data?.preserveOnClose, dispatch]);

  const handleDeactivateAccount = useCallback(() => {
    if (account) {
      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            heading: "Deactivate Company",
            content: `Are you sure you want to deactivate ${account.name}?`,
            cancelBtnText: "Cancel",
            successBtnText: "Deactivate",
            successBtnStyle: "clear-red",
            onSuccess: () => {
              dispatch(accountStore.actions.deactivateAccount(account?.id));
              dispatch(sharedActions.hideOverlay());
            },
          },
        }),
      );
    }
  }, [account, dispatch]);

  const handleActivateAccount = useCallback(() => {
    if (account) {
      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            heading: "Activate Company",
            content: `Are you sure you want to activate ${account.name}?`,
            cancelBtnText: "Cancel",
            successBtnText: "Activate",
            successBtnStyle: "get-output",
            onSuccess: () => {
              dispatch(accountStore.actions.activateAccount(account?.id));
              dispatch(sharedActions.hideOverlay());
            },
          },
        }),
      );
    }
  }, [account, dispatch]);

  const changeFormValues = useCallback(() => {
    dispatch(sharedActions.updateOverlay({ leaveConfirm: true }));
  }, [dispatch]);

  const handleSubmitForm = useCallback(
    (payload: CreateAccountInterface, message?: string) => {
      if (account) {
        return dispatch(
          accountStore.actions.updateAccount.request({
            ...payload,
            message,
            id: Number(account.id),
            callback: () => {
              if (payload.hubspotId && payload.hubspotId !== account.hubspot_company_id?.toString()) {
                dispatch(
                  AccountActions.syncAccount.request({
                    id: account?.id,
                    callback: () => dispatch(AccountActions.getAccount.request({ id: account.id })),
                  }),
                );
              }

              if (payload.chargebeeId && payload.chargebeeId !== account.chargebee_customer_id) {
                dispatch(AccountActions.getAccount.request({ id: account.id }));
              }

              callback?.();
            },
          }),
        );
      }

      return dispatch(
        accountStore.actions.createAccount.request({
          ...payload,
          message,
          callback,
        }),
      );
    },
    [dispatch, account, callback],
  );

  return (
    <div>
      <CompanyForm
        account={selectedAccount}
        onDelete={handleDeactivateAccount}
        onActivate={handleActivateAccount}
        onChangeForm={changeFormValues}
        onCloseForm={onClose}
        onSubmitForm={handleSubmitForm}
      />
    </div>
  );
};

export default CompanyCreateEditContainer;
